import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { concatMap, takeUntil, toArray } from 'rxjs/operators';
import { from } from 'rxjs';
import { TituloDTO } from '../../../../models/dashboard/titulo.model';
import { TituloService, MovimentoBaixaTituloDTO } from '../../../../services/dashboard/titulo.service';
import { ContaService } from '../../../../services/conta/conta.service';
import { CotacaoGuardService } from '../../../../services/cotacao/cotacao-guard.service';
import { CotacaoService } from '../../../../services/cotacao/cotacao.service';
import { EmpresaConfigService } from '../../../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../../../models/configuracoes/empresa.model';
import { FormaPagamentoConfig, FormaPagamentoResult, DadosPagamentoConta, DadosPagamentoDinheiro } from '../../../../shared/components/forma-pagamento-dialog/forma-pagamento-dialog.interfaces';
import { PaginatorState } from 'primeng/paginator';
import { toLocalISOString } from '../../../../shared/utils/date-utils';
import { PeriodoFiltro, TipoPeriodo } from '../../../../models/dashboard/periodo-filtro.model';
import { PeriodoFiltroComponent } from '../../../../shared/components/periodo-filtro/periodo-filtro.component';

type TipoOperacao = 'ENTRADA' | 'SAIDA';
type SituacaoTitulo = 'ABERTO' | 'PAGO';
/** Tela que abriu os titulos da pessoa (define para onde o "voltar" leva). */
type OrigemTitulosPessoa = 'clientes' | 'fornecedores' | 'contador-area' | 'contador-operar';

/**
 * Origem -> rota de volta e rotulo da raiz do breadcrumb.
 *
 * As duas telas do contador entram aqui porque a ficha de titulos e alcancada
 * dos gastos acessorios: sem isto o "voltar" despejava o contador em Contas a
 * Receber, uma tela que ele nem estava usando.
 */
const DESTINO_POR_ORIGEM: Record<OrigemTitulosPessoa, { rota: string; rotulo: string }> = {
  'clientes': { rota: '/clientes', rotulo: 'clientes.texts.titulo_criar' },
  'fornecedores': { rota: '/fornecedores', rotulo: 'fornecedores.texts.titulo_criar' },
  'contador-area': { rota: '/contador/area', rotulo: 'contador.dashboard.titulo' },
  'contador-operar': { rota: '/contador/operar', rotulo: 'contador_operar.titulo' },
};

@Component({
  selector: 'app-titulos-pessoa',
  templateUrl: './titulos-pessoa.component.html',
  styleUrls: ['./titulos-pessoa.component.scss'],
  providers: [MessageService],
})
export class TitulosPessoaComponent implements OnInit, OnDestroy {

  // Dados da rota
  pessoaId!: number;
  pessoaNome = '';
  tipoOperacao: TipoOperacao = 'ENTRADA';

  // Dados
  titulos: TituloDTO[] = [];
  totalRecords = 0;

  // Paginacao
  first = 0;
  rows = 10;
  page = 0;

  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // Ordenacao server-side (backend sort).
  // Vazio = sem ordenacao explicita (backend usa a ordem padrao). Preenchido
  // quando o usuario clica em um cabecalho ordenavel da tabela.
  sortField = '';
  sortOrder = 1; // 1 = asc, -1 = desc

  // Loading
  loading = false;
  processandoBatch = false;

  // Dialog de forma de pagamento
  dialogVisible = false;
  tituloParaReceber: TituloDTO | null = null;
  processandoRecebimento = false;

  // Modo batch: quando true, o dialog confirma todos os selecionados
  dialogModoBatch = false;

  // Dialog intermediario de valor (antes do forma-pagamento-dialog)
  dialogValorVisible = false;
  valorDigitado = 0;
  valorMaximo = 0;

  // Mantido para compatibilidade batch (pode ser removido no futuro)
  titulosSelecionados: TituloDTO[] = [];

  /**
   * Moeda padrao da empresa logada (codigo ISO).
   * Usada para inicializar `formaPagamentoConfig.moedaPadrao` — aplica a
   * matriz de pareamento correta para a empresa (resolve item #27 do
   * relatorio master, antes hardcoded como 'PYG').
   * Carregada em `carregarMoedaEmpresa()` durante `ngOnInit`.
   */
  moedaEmpresa: 'PYG' | 'BRL' | 'USD' | 'ARS' = 'PYG';
  /** Flag que controla se o dialog pode ser aberto (aguarda moeda da empresa). */
  empresaCarregada = false;

  /**
   * Moeda do(s) titulo(s) que estao sendo baixados (codigo do enum backend:
   * 'GUARANI' | 'REAL' | 'DOLAR' | 'PESO'). Setada antes de abrir o dialog
   * em `abrirDialogRecebimento` (modo individual) e `receberVarios` (modo
   * batch — apos validar que todos selecionados tem a mesma moeda).
   *
   * Propagada para `<app-forma-pagamento-dialog [moedaNota]>`, que aplica a
   * matriz de pareamento (`MOEDAS_COMPATIVEIS`) ao dropdown de contas via
   * `contasFiltradas`. Espelha o callsite de `fornecedor-compra` que ja usa
   * o mesmo padrao (`[moedaNota]="moedaPadrao?.enum"`).
   */
  moedaTitulo: 'GUARANI' | 'REAL' | 'DOLAR' | 'PESO' | undefined = undefined;

  // Configuracao do forma-pagamento-dialog
  formaPagamentoConfig: FormaPagamentoConfig = {
    formasPermitidas: ['dinheiro', 'conta'],
    moedaPadrao: 'PYG',
    habilitarMultiMoeda: true,
    cotacoes: { cotUSD: 1, cotPYG: 7180, cotBRL: 5.47 },
  };

  // Total a pagar exibido no dialog
  totalAPagarDialog = 0;

  // Contas bancarias para o dialog
  contasFormatadas: { label: string; value: any }[] = [];
  // Contas EFETIVO (caixa) por moeda - para pagamentos em dinheiro
  contasEfetivo: any[] = [];

  // Cotacoes
  cotacaoId = 0;
  cotUSD = 1;
  cotPYG = 7180;
  cotBRL = 5.47;
  /** Cotacao Peso Argentino em Guaranis (1 ARS = N PYG). 0 = peso desabilitado. */
  cotPesoGuarani = 0;
  /** Cotacao direta BRL->PYG (1 BRL = N PYG). 0 = triangulacao via USD. */
  cotRealGuarani = 0;

  // Resultado do ultimo dialog de forma de pagamento
  private ultimoResultadoPagamento: FormaPagamentoResult | null = null;

  // Periodo (recebido via queryParam do pai)
  dataHoraInicial = '';
  dataHoraFinal = '';
  // Periodo escolhido interativamente no app-periodo-filtro (null = nao houve
  // interacao ainda; usado por `periodoEhTodos`/`hasFiltroAtivo` para decidir
  // o empty state contextual).
  periodoTipoAtivo: TipoPeriodo | number | null = null;
  // Preset de periodo herdado do pai (queryParam periodoTipo). Alimenta o
  // [valorPadrao] do app-periodo-filtro para o CONTROLE nascer exibindo o mesmo
  // recorte dos DADOS. null = nao herdou preset (dashboard/custom) -> fallback showAll.
  periodoTipoHerdado: TipoPeriodo | null = null;
  situacao: SituacaoTitulo | '' = '';
  contaId: number | null = null;

  // Titulo clicado na listagem-pai: linha correspondente ganha destaque sutil.
  tituloDestacadoId: number | null = null;
  // Origem da navegacao (queryParam `origem`); null = listagem de contas
  origem: OrigemTitulosPessoa | null = null;

  /** Cliente a reabrir no Operar Cliente (queryParam `clienteId`). */
  clienteDaOrigem: number | null = null;

  // Traducoes
  texts = {
    titulo_titulos_pessoa: '',
    subtitulo_titulos_pessoa: '',
  };

  labels = {
    pessoa: '',
    numero_titulo: '',
    valor: '',
    valor_total: '',
    saldo: '',
    vencimento: '',
    status: '',
    mes_referencia: '',
    registro_recebidos: '',
    registro_pagos: '',
    recebido: '',
    pago_label: '',
    devolvido: '',
    aberto: '',
    pago_status: '',
    cancelado: '',
    selecionados: '',
    total_selecionados: '',
    valor_a_receber: '',
    valor_a_pagar: '',
    saldo_titulo: '',
    pagamentos_titulo: '',
    data_pagamento: '',
    valor_pago: '',
    total: '',
    recebidos: '',
    limpar_filtros: '',
    itens_por_pagina: '',
    acoes: '',
  };

  // Tooltips das acoes da tabela (explicam cada icone da coluna "Acoes").
  tooltips = {
    cobrar: '',
    pagar: '',
    ver_fatura: '',
    ver_despesa: '',
    ver_contrato: '',
    // Exibido no botao de receber DESABILITADO de um titulo que veio de uma
    // parcela de contrato do contador (so pode ser recebido ao faturar a parcela).
    receber_bloqueado_contrato: '',
  };

  botoes = {
    voltar: '',
    receber: '',
    pagar: '',
    receber_varios: '',
    pagar_varios: '',
    confirmar_recebimento: '',
    confirmar_pagamento: '',
    confirmar: '',
  };

  mensagens = {
    recebimento_sucesso: '',
    pagamento_sucesso: '',
    recebimento_erro: '',
    pagamento_erro: '',
    recebimento_batch_sucesso: '',
    pagamento_batch_sucesso: '',
    nenhum_titulo_selecionado: '',
    nenhum_titulo: '',
    titulos_moedas_diferentes: '',
    valor_maior_saldo: '',
    valor_obrigatorio: '',
    nenhum_titulo_filtro: '',
    nenhum_titulo_filtro_dica: '',
    // Aviso quando o "receber varios" remove da selecao titulos de parcela de
    // contrato (defesa contra o "selecionar todos" do cabecalho da tabela).
    recebimento_bloqueado_contrato_lote: '',
  };

  // Referencia ao controle de periodo para resetar sua exibicao ao limpar filtros
  // (o controle so le [valorPadrao] no init; nao reage a mudanca externa depois).
  @ViewChild(PeriodoFiltroComponent) private periodoFiltro?: PeriodoFiltroComponent;

  private destroy$ = new Subject<void>();
  private langChangeSub!: Subscription;

  constructor(
    private tituloService: TituloService,
    private contaService: ContaService,
    private cotacaoService: CotacaoService,
    private cotacaoGuard: CotacaoGuardService,
    private empresaService: EmpresaConfigService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Extrair params da rota
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.pessoaId = Number(params['pessoaId']);
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.pessoaNome = params['nome'] || '';
      this.tipoOperacao = (params['tipo'] as TipoOperacao) || 'ENTRADA';
      this.dataHoraInicial = params['dataInicio'] || '';
      this.dataHoraFinal = params['dataFim'] || '';
      this.situacao = (params['situacao'] as SituacaoTitulo) || '';
      this.contaId = params['conta'] ? Number(params['conta']) : null;
      this.tituloDestacadoId = params['titulo'] ? Number(params['titulo']) : null;
      this.origem = params['origem'] in DESTINO_POR_ORIGEM
        ? (params['origem'] as OrigemTitulosPessoa)
        : null;
      // So o Operar Cliente precisa saber PARA QUAL cliente voltar.
      this.clienteDaOrigem = params['clienteId'] ? Number(params['clienteId']) : null;
      // Semeia o estado de exibicao do periodo a partir do preset herdado do pai.
      // Sem emissao nem recarga extra: os dados ja carregam pelas datas herdadas
      // (dataInicio/dataFim). Isso apenas mantem controle+chip coerentes com os dados.
      const periodoTipoParam = params['periodoTipo'] as TipoPeriodo | undefined;
      this.periodoTipoHerdado = periodoTipoParam ?? null;
      this.periodoTipoAtivo = periodoTipoParam ?? null;
    });

    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    this.carregarTitulos();
    this.carregarContas();
    this.carregarCotacao();
    this.carregarMoedaEmpresa();
  }

  /**
   * Carrega a moeda padrao da empresa logada e atualiza
   * `formaPagamentoConfig.moedaPadrao` (resolve item #27 — antes hardcoded
   * como 'PYG' fazendo o dialog mostrar cards errados para empresas
   * BRL/USD/ARS).
   */
  private carregarMoedaEmpresa(): void {
    this.empresaService.getById('/configuracaoEmpresa/sessao').subscribe({
      next: (empresa: EmpresaConfigDTO) => {
        const iso = this.derivarMoedaIsoEmpresa(empresa);
        if (iso) {
          this.moedaEmpresa = iso;
        }
        this.empresaCarregada = true;
        this.atualizarConfigFormaPagamento();
      },
      error: () => {
        // Mantem fallback 'PYG' se falhar (backwards-compat).
        this.empresaCarregada = true;
      },
    });
  }

  /** Mapeia o enum de moeda da empresa (GUARANI/REAL/DOLAR/PESO) para ISO. */
  private derivarMoedaIsoEmpresa(empresa: EmpresaConfigDTO): 'PYG' | 'BRL' | 'USD' | 'ARS' | undefined {
    const moeda: any = empresa?.moeda;
    if (!moeda) return undefined;
    if (moeda.codigoOperacao) {
      return moeda.codigoOperacao as 'PYG' | 'BRL' | 'USD' | 'ARS';
    }
    const enumStr = (typeof moeda === 'object' && moeda.enum)
      ? String(moeda.enum).toUpperCase()
      : String(moeda).toUpperCase();
    if (enumStr === 'GUARANI') return 'PYG';
    if (enumStr === 'REAL') return 'BRL';
    if (enumStr === 'DOLAR') return 'USD';
    if (enumStr === 'PESO') return 'ARS';
    return undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // --- Traducoes ---

  private loadTranslations(): void {
    this.texts = {
      titulo_titulos_pessoa: this.translate.instant('contas-cobrar.texts.titulo_titulos_pessoa'),
      subtitulo_titulos_pessoa: this.translate.instant('contas-cobrar.texts.subtitulo_titulos_pessoa'),
    };

    this.labels = {
      pessoa: this.translate.instant('contas-cobrar.labels.pessoa'),
      numero_titulo: this.translate.instant('contas-cobrar.labels.numero_titulo'),
      valor: this.translate.instant('contas-cobrar.labels.valor'),
      valor_total: this.translate.instant('contas-cobrar.labels.valor_total'),
      saldo: this.translate.instant('contas-cobrar.labels.saldo'),
      vencimento: this.translate.instant('contas-cobrar.labels.vencimento'),
      status: this.translate.instant('contas-cobrar.labels.status'),
      mes_referencia: this.translate.instant('contas-cobrar.labels.mes_referencia'),
      registro_recebidos: this.translate.instant('contas-cobrar.labels.registro_recebidos'),
      registro_pagos: this.translate.instant('contas-cobrar.labels.registro_pagos'),
      recebido: this.translate.instant('contas-cobrar.labels.recebido'),
      pago_label: this.translate.instant('contas-cobrar.labels.pago_label'),
      devolvido: this.translate.instant('contas-cobrar.labels.valor_devolvido'),
      aberto: this.translate.instant('contas-cobrar.labels.aberto'),
      pago_status: this.translate.instant('contas-cobrar.labels.pago'),
      cancelado: this.translate.instant('contas-cobrar.labels.cancelado'),
      selecionados: this.translate.instant('contas-cobrar.labels.selecionados'),
      total_selecionados: this.translate.instant('contas-cobrar.labels.total_selecionados'),
      valor_a_receber: this.translate.instant('contas-cobrar.labels.valor_a_receber'),
      valor_a_pagar: this.translate.instant('contas-cobrar.labels.valor_a_pagar'),
      saldo_titulo: this.translate.instant('contas-cobrar.labels.saldo_titulo'),
      pagamentos_titulo: this.translate.instant('contas-cobrar.labels.pagamentos_titulo'),
      data_pagamento: this.translate.instant('contas-cobrar.labels.data_pagamento'),
      valor_pago: this.translate.instant('contas-cobrar.labels.valor_pago'),
      total: this.translate.instant('contas-cobrar.labels.total'),
      recebidos: this.translate.instant('contas-cobrar.labels.recebidos'),
      limpar_filtros: this.translate.instant('contas-cobrar.labels.limpar_filtros'),
      itens_por_pagina: this.translate.instant('contas-pagar-receber.labels.itens_por_pagina'),
      acoes: this.translate.instant('contas-cobrar.labels.acoes'),
    };

    this.tooltips = {
      cobrar: this.translate.instant('contas-cobrar.tooltips.cobrar'),
      pagar: this.translate.instant('contas-cobrar.tooltips.pagar'),
      ver_fatura: this.translate.instant('contas-cobrar.tooltips.ver_fatura'),
      ver_despesa: this.translate.instant('contas-cobrar.tooltips.ver_despesa'),
      ver_contrato: this.translate.instant('contas-cobrar.tooltips.ver_contrato'),
      receber_bloqueado_contrato: this.translate.instant('contas-cobrar.tooltips.receber_bloqueado_contrato'),
    };

    this.botoes = {
      voltar: this.translate.instant('contas-cobrar.botoes.voltar'),
      receber: this.translate.instant('contas-cobrar.botoes.receber'),
      pagar: this.translate.instant('contas-cobrar.botoes.pagar'),
      receber_varios: this.translate.instant('contas-cobrar.botoes.receber_varios'),
      pagar_varios: this.translate.instant('contas-cobrar.botoes.pagar_varios'),
      confirmar_recebimento: this.translate.instant('contas-cobrar.botoes.confirmar_recebimento'),
      confirmar_pagamento: this.translate.instant('contas-cobrar.botoes.confirmar_pagamento'),
      confirmar: this.translate.instant('common.confirmar'),
    };

    this.mensagens = {
      recebimento_sucesso: this.translate.instant('contas-cobrar.mensagens.recebimento_sucesso'),
      pagamento_sucesso: this.translate.instant('contas-cobrar.mensagens.pagamento_sucesso'),
      recebimento_erro: this.translate.instant('contas-cobrar.mensagens.recebimento_erro'),
      pagamento_erro: this.translate.instant('contas-cobrar.mensagens.pagamento_erro'),
      recebimento_batch_sucesso: this.translate.instant('contas-cobrar.mensagens.recebimento_batch_sucesso'),
      pagamento_batch_sucesso: this.translate.instant('contas-cobrar.mensagens.pagamento_batch_sucesso'),
      nenhum_titulo_selecionado: this.translate.instant('contas-cobrar.mensagens.nenhum_titulo_selecionado'),
      nenhum_titulo: this.translate.instant('contas-cobrar.mensagens.nenhum_titulo'),
      titulos_moedas_diferentes: this.translate.instant('contas-cobrar.mensagens.titulos_moedas_diferentes'),
      valor_maior_saldo: this.translate.instant('contas-cobrar.mensagens.valor_maior_saldo'),
      valor_obrigatorio: this.translate.instant('contas-cobrar.mensagens.valor_obrigatorio'),
      nenhum_titulo_filtro: this.translate.instant('contas-cobrar.mensagens.nenhum_titulo_filtro'),
      nenhum_titulo_filtro_dica: this.translate.instant('contas-cobrar.mensagens.nenhum_titulo_filtro_dica'),
      recebimento_bloqueado_contrato_lote: this.translate.instant('contas-cobrar.mensagens.recebimento_bloqueado_contrato_lote'),
    };

    this.atualizarConfigFormaPagamento();
  }

  // --- Titulo da pagina ---

  get tituloHeader(): string {
    const template = this.texts.titulo_titulos_pessoa || 'Titulos - {nome}';
    return template.replace('{nome}', this.pessoaNome);
  }

  get botaoAcao(): string {
    return this.tipoOperacao === 'ENTRADA' ? this.botoes.receber : this.botoes.pagar;
  }

  get botaoAcaoVarios(): string {
    return this.tipoOperacao === 'ENTRADA' ? this.botoes.receber_varios : this.botoes.pagar_varios;
  }

  /** Tooltip do botao-icone de acao (cobrar/pagar) conforme o tipo de operacao. */
  get tooltipAcao(): string {
    return this.tipoOperacao === 'ENTRADA' ? this.tooltips.cobrar : this.tooltips.pagar;
  }

  /**
   * Regra (spec): um titulo originado de uma PARCELA de contrato do contador NAO
   * pode ser recebido diretamente nesta tela. Ele so vira recebivel quando a
   * parcela e faturada — ai o faturamento cancela este titulo de controle e gera
   * um novo titulo (ligado a nota, `contratocontador_id == null`) que pode ser
   * recebido normalmente. Vale SOMENTE no lado ENTRADA (recebimento do contador);
   * o lado a pagar do cliente (SAIDA) fica intacto.
   */
  isRecebimentoBloqueadoPorContrato(titulo: TituloDTO): boolean {
    return this.tipoOperacao === 'ENTRADA' && this.temOrigemContratoContador(titulo);
  }

  /**
   * Tooltip do botao de acao (cobrar/pagar) para uma linha. Quando o recebimento
   * esta bloqueado por origem em parcela de contrato, explica ao usuario o porque
   * (botao aparece desabilitado). Caso contrario, usa o tooltip padrao da acao.
   */
  tooltipBotaoAcao(titulo: TituloDTO): string {
    if (this.isRecebimentoBloqueadoPorContrato(titulo)) {
      return this.tooltips.receber_bloqueado_contrato;
    }
    return this.tooltipAcao;
  }

  get totalSelecionado(): number {
    return this.titulosSelecionados.reduce((sum, t) => sum + (t.saldo || t.valor), 0);
  }

  get labelValorDialog(): string {
    return this.tipoOperacao === 'ENTRADA' ? this.labels.valor_a_receber : this.labels.valor_a_pagar;
  }

  /** Retorna true se a moeda do titulo eh GUARANI (sem decimais) */
  get moedaSemDecimais(): boolean {
    const titulo = this.tituloParaReceber || (this.titulosSelecionados.length > 0 ? this.titulosSelecionados[0] : null);
    if (!titulo) return true;
    const moeda = this.extrairMoeda(titulo.moeda);
    return moeda === 'GUARANI';
  }

  get labelSelecionados(): string {
    const template = this.labels.selecionados || '{count} titulo(s) selecionado(s)';
    return template.replace('{count}', String(this.titulosSelecionados.length));
  }

  get labelBotaoConfirmarDialog(): string {
    // Botao de confirmacao do dialogo "Valor a receber/pagar": label generico
    // "Confirmar" (common.confirmar) para nao quebrar em duas linhas e seguir
    // o padrao dos demais dialogos do sistema.
    return this.botoes.confirmar;
  }

  // --- Carregar dados ---

  carregarTitulos(): void {
    this.loading = true;
    this.tituloService.buscarTitulosPorPessoa({
      page: this.page,
      size: this.rows,
      tipoOperacaoFinanceira: this.tipoOperacao,
      pessoaId: this.pessoaId,
      situacao: this.situacao || undefined,
      conta: this.contaId || undefined,
      dataHoraInicial: this.dataHoraInicial || undefined,
      dataHoraFinal: this.dataHoraFinal || undefined,
      sort: this.buildSortParam(),
    }).subscribe({
      next: (response) => {
        this.titulos = response.content;
        this.totalRecords = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        // Backend retorna 404 quando nao ha titulos - tratar como lista vazia
        if (err?.status === 404) {
          this.titulos = [];
          this.totalRecords = 0;
          return;
        }
        const msg = this.tipoOperacao === 'ENTRADA'
          ? this.mensagens.recebimento_erro
          : this.mensagens.pagamento_erro;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: msg,
        });
      },
    });
  }

  private carregarContas(): void {
    this.contaService.get('/conta', { page: 0, size: 100 }).subscribe({
      next: (response: any) => {
        if (response && response.content) {
          const todasContas = response.content;
          this.contasFormatadas = todasContas
            .filter((conta: any) => conta.tipoConta === 'BANCO' || conta.tipoConta?.enum === 'BANCO')
            .map((conta: any) => ({
              label: conta.tituloDaConta,
              value: conta,
            }));
          // Contas EFETIVO (caixa) - numeroConta === 0
          this.contasEfetivo = todasContas.filter((conta: any) =>
            conta.numeroConta === 0 || conta.tipoConta === 'EFETIVO' || conta.tipoConta?.enum === 'EFETIVO'
          );
        }
      },
      error: () => {
        this.contasFormatadas = [];
        this.contasEfetivo = [];
      },
    });
  }

  private carregarCotacao(): void {
    this.cotacaoService.get('/cotacao/ultimaCotacao').subscribe({
      next: (res: any) => {
        if (res) {
          this.cotacaoId = res.id || 0;
          this.cotPYG = res.dolarGuarani || 7180;
          this.cotBRL = res.dolarReal || 5.47;
          // pesoGuarani e opcional; sem valor, coluna peso permanece oculta
          this.cotPesoGuarani = res.pesoGuarani || 0;
          // realGuarani: cotacao direta BRL->PYG; 0 = fallback triangulacao
          this.cotRealGuarani = res.realGuarani || 0;
          this.atualizarConfigFormaPagamento();
        }
      },
      error: () => {
        // Manter valores padrao
      },
    });
  }

  /** Solicita a cotacao de hoje quando nao cadastrada e atualiza id/taxas. false = cancelou. */
  private async garantirCotacaoDoDia(): Promise<boolean> {
    const cotacao = await this.cotacaoGuard.garantirCotacao();
    if (!cotacao?.id) {
      return false;
    }
    this.cotacaoId = cotacao.id;
    this.cotPYG = cotacao.dolarGuarani || this.cotPYG;
    this.cotBRL = cotacao.dolarReal || this.cotBRL;
    this.cotPesoGuarani = cotacao.pesoGuarani || 0;
    this.cotRealGuarani = cotacao.realGuarani || 0;
    return true;
  }

  private atualizarConfigFormaPagamento(): void {
    const labelBotao = this.tipoOperacao === 'ENTRADA'
      ? this.botoes.confirmar_recebimento
      : this.botoes.confirmar_pagamento;

    this.formaPagamentoConfig = {
      formasPermitidas: ['dinheiro', 'conta'],
      // Spec 06: respeita moeda da empresa logada (antes hardcoded 'PYG').
      // Fallback 'PYG' enquanto empresa nao carregou (backwards-compat).
      moedaPadrao: this.moedaEmpresa,
      habilitarMultiMoeda: true,
      cotacoes: {
        cotUSD: this.cotUSD,
        cotPYG: this.cotPYG,
        cotBRL: this.cotBRL,
        cotPesoGuarani: this.cotPesoGuarani,
        cotRealGuarani: this.cotRealGuarani,
      },
      labelBotaoConfirmar: labelBotao || undefined,
      iconeBotaoConfirmar: 'pi pi-check-circle',
    };
  }

  // --- Paginacao ---

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor(this.first / this.rows);
    this.carregarTitulos();
  }

  /** Alteracao no seletor de "Itens por pagina" — volta para a primeira pagina */
  onRowsPerPageChange(): void {
    this.first = 0;
    this.page = 0;
    this.carregarTitulos();
  }

  // --- Ordenacao (server-side) ---

  /**
   * Monta a string de ordenacao no formato Spring "campo,direcao" (ex.:
   * "vencimento,desc"). Retorna undefined quando nenhuma coluna foi escolhida,
   * para que o service NAO envie o parametro `sort`.
   */
  private buildSortParam(): string | undefined {
    if (!this.sortField) return undefined;
    const direction = this.sortOrder === 1 ? 'asc' : 'desc';
    return `${this.sortField},${direction}`;
  }

  /**
   * Recebe o evento (onSort) da p-table (modo customSort, sem ordenacao no
   * cliente) e refaz a busca no servidor. A lista e paginada no backend, entao
   * a ordenacao TEM de ser server-side (ordenar so a pagina atual enganaria o
   * usuario). Espelha o `onSortChanged` da tela-pai: zera a pagina e recarrega.
   * O guard evita recarga desnecessaria quando o evento repete o mesmo estado.
   */
  onSortChanged(event: { field?: string; order?: number }): void {
    if (!event.field) return;
    const order = event.order ?? 1;
    if (event.field === this.sortField && order === this.sortOrder) return;
    this.sortField = event.field;
    this.sortOrder = order;
    this.first = 0;
    this.page = 0;
    this.carregarTitulos();
  }

  // --- Filtro de periodo (interativo) ---

  /**
   * Recebe o periodo escolhido no `app-periodo-filtro` e refaz a busca. Do lado
   * do front so enviamos `dataHoraInicial`/`dataHoraFinal`; a semantica do
   * recorte (por vencimento) e responsabilidade do backend. Espelha o
   * `onPeriodoChange` da tela-pai: atualiza as datas, zera a pagina e recarrega.
   */
  onPeriodoChange(periodo: PeriodoFiltro): void {
    this.dataHoraInicial = periodo.dataInicio;
    this.dataHoraFinal = periodo.dataFim;
    // Guarda o tipo do periodo escolhido para `periodoEhTodos`/`hasFiltroAtivo`
    // decidirem o empty state contextual (recorte real vs "Mostrar todos").
    this.periodoTipoAtivo = periodo.tipo;
    this.first = 0;
    this.page = 0;
    this.carregarTitulos();
  }

  // --- Recebimento individual ---

  abrirDialogRecebimento(titulo: TituloDTO): void {
    // Guarda defensiva: titulo de parcela de contrato so pode ser recebido ao
    // faturar a parcela (o botao ja nasce desabilitado, isto so blinda o metodo).
    if (this.isRecebimentoBloqueadoPorContrato(titulo)) {
      return;
    }
    this.tituloParaReceber = titulo;
    this.dialogModoBatch = false;
    this.valorMaximo = titulo.saldo || titulo.valor;
    this.valorDigitado = this.valorMaximo;
    // Spec 07: alimenta [moedaNota] do dialog para ativar matriz de
    // pareamento no dropdown de contas (contasFiltradas).
    this.moedaTitulo = this.toMoedaTitulo(this.extrairMoeda(titulo.moeda));
    this.dialogValorVisible = true;
  }

  // --- Recebimento batch ---

  receberVarios(): void {
    // Defesa em profundidade: remove da selecao titulos de parcela de contrato,
    // que nao podem ser recebidos direto (poderiam entrar via "selecionar todos"
    // do cabecalho, que ignora o [disabled] do checkbox individual).
    const bloqueados = this.titulosSelecionados.filter(t => this.isRecebimentoBloqueadoPorContrato(t));
    if (bloqueados.length > 0) {
      this.titulosSelecionados = this.titulosSelecionados.filter(t => !this.isRecebimentoBloqueadoPorContrato(t));
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('common.atencao') || 'Atencion',
        detail: this.mensagens.recebimento_bloqueado_contrato_lote,
      });
    }

    if (this.titulosSelecionados.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao') || 'Atencion',
        detail: this.mensagens.nenhum_titulo_selecionado,
      });
      return;
    }

    // Validar se todos os títulos selecionados possuem a mesma moeda
    const moedasDistintas = new Set(this.titulosSelecionados.map(t => this.extrairMoeda(t.moeda)));
    if (moedasDistintas.size > 1) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao') || 'Atencion',
        detail: this.mensagens.titulos_moedas_diferentes,
      });
      return;
    }

    this.tituloParaReceber = null;
    this.dialogModoBatch = true;
    this.valorMaximo = this.totalSelecionado;
    this.valorDigitado = this.valorMaximo;
    // Spec 07: alimenta [moedaNota] do dialog. Acima ja garantimos via
    // `moedasDistintas.size > 1` que todos os titulos selecionados tem a
    // mesma moeda, entao podemos usar o primeiro como referencia.
    this.moedaTitulo = this.toMoedaTitulo(this.extrairMoeda(this.titulosSelecionados[0].moeda));
    this.dialogValorVisible = true;
  }

  // --- Confirmar valor e abrir dialog de forma de pagamento ---

  async confirmarValor(): Promise<void> {
    if (!this.valorDigitado || this.valorDigitado <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao') || 'Atencion',
        detail: this.mensagens.valor_obrigatorio,
      });
      return;
    }

    if (this.valorDigitado > this.valorMaximo) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao') || 'Atencion',
        detail: this.mensagens.valor_maior_saldo,
      });
      return;
    }

    // Garante a cotacao do dia ANTES do pagamento (solicita se nao houver).
    if (!(await this.garantirCotacaoDoDia())) {
      return;
    }

    this.dialogValorVisible = false;
    this.totalAPagarDialog = this.valorDigitado;
    this.atualizarConfigFormaPagamento();
    this.dialogVisible = true;
  }

  cancelarDialogValor(): void {
    this.dialogValorVisible = false;
    this.tituloParaReceber = null;
  }

  // --- Confirmar pagamento (individual ou batch) ---

  async onConfirmarPagamento(result: FormaPagamentoResult): Promise<void> {
    this.ultimoResultadoPagamento = result;
    // Rede de seguranca: sem cotacao valida nao ha baixa (cotacaoId 0 nunca vai ao backend).
    if (!this.cotacaoId && !(await this.garantirCotacaoDoDia())) {
      return;
    }
    if (this.dialogModoBatch) {
      this.processarBatch();
    } else {
      this.processarIndividual();
    }
  }

  private construirMovimentoBaixa(
    valor: number,
    pessoaId: number,
    moeda: string,
    tiposDeCobranca: { tituloId: number; valor: number }[]
  ): MovimentoBaixaTituloDTO {
    const result = this.ultimoResultadoPagamento;
    const ehEntrada = this.tipoOperacao === 'ENTRADA';

    let contaId: number | null = null;
    let valorRecebidoNaConta = 0;
    let valorRecebidoEmGuarani = 0;
    let valorRecebidoEmDolar = 0;
    let valorRecebidoEmReal = 0;
    let valorRecebidoEmPeso = 0;

    if (result?.forma === 'conta') {
      const dados = result.dados as DadosPagamentoConta;
      contaId = dados.conta_id;
      valorRecebidoNaConta = dados.valorRecebidoNaConta;
    } else if (result?.forma === 'dinheiro') {
      const dados = result.dados as DadosPagamentoDinheiro;
      valorRecebidoEmGuarani = dados.valorRecebidoEmGuarani;
      valorRecebidoEmDolar = dados.valorRecebidoEmDolar;
      valorRecebidoEmReal = dados.valorRecebidoEmReal;
      valorRecebidoEmPeso = dados.valorRecebidoEmPeso || 0;
    } else {
      // Fallback: sem forma de pagamento selecionada, usar valor total em guarani
      valorRecebidoEmGuarani = valor;
    }

    const body: MovimentoBaixaTituloDTO = {
      tipoDeTransacao: ehEntrada ? 'RECEITA' : 'DESPESA',
      // Fallback: o backend sobrescreve pelo tipo do titulo (`herdarTipoContabilDosTitulos`).
      // So prevalecem quando os titulos da baixa nao tem tipo, ou tem tipos diferentes.
      tipoReceita: ehEntrada ? 'VENDAS_A_PRAZO' : undefined,
      tipoDespesa: !ehEntrada ? 'COMPRAS_DE_MERCADORIAS' : undefined,
      valor,
      cotacaoId: this.cotacaoId,
      contaId,
      pessoaId,
      moedaDeOrigem: moeda || 'GUARANI',
      cancelado: false,
      emissao: toLocalISOString(),
      baixas: [],
      tipoDeCobranca: tiposDeCobranca,
      valorRecebidoNaConta,
      valorRecebidoEmGuarani,
      valorRecebidoEmDolar,
      valorRecebidoEmReal,
      valorRecebidoEmPeso,
    };

    console.log('BODY MOVIMENTO:', JSON.stringify(body));
    return body;
  }

  // Extrai a string do enum de moeda, pois o backend serializa enums como objetos { enum: 'GUARANI' }
  private extrairMoeda(moeda: any): string {
    if (!moeda) return 'GUARANI';
    if (typeof moeda === 'string') return moeda;
    if (moeda && typeof moeda === 'object' && moeda.enum) return moeda.enum;
    return 'GUARANI';
  }

  /**
   * Normaliza a moeda do titulo para o subset aceito pelo
   * `[moedaNota]` do `app-forma-pagamento-dialog`. Retorna `undefined` se
   * a moeda nao for reconhecida — neste caso o dialog mantem comportamento
   * antigo (sem filtrar por matriz, fallback conservador).
   */
  private toMoedaTitulo(
    moeda: string,
  ): 'GUARANI' | 'REAL' | 'DOLAR' | 'PESO' | undefined {
    if (moeda === 'GUARANI' || moeda === 'REAL' || moeda === 'DOLAR' || moeda === 'PESO') {
      return moeda;
    }
    return undefined;
  }

  private processarIndividual(): void {
    if (!this.tituloParaReceber) return;

    this.processandoRecebimento = true;
    const titulo = this.tituloParaReceber;
    const valor = this.valorDigitado;

    const body = this.construirMovimentoBaixa(
      valor,
      titulo.pessoaId || titulo.pessoa_id || this.pessoaId,
      this.extrairMoeda(titulo.moeda),
      [{ tituloId: titulo.id, valor }]
    );

    this.tituloService.gerenciarTituloAberto(body).subscribe({
      next: () => {
        this.processandoRecebimento = false;
        this.dialogVisible = false;
        this.tituloParaReceber = null;
        const msg = this.tipoOperacao === 'ENTRADA'
          ? this.mensagens.recebimento_sucesso
          : this.mensagens.pagamento_sucesso;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: msg,
        });
        this.carregarTitulos();
      },
      error: () => {
        this.processandoRecebimento = false;
        const msg = this.tipoOperacao === 'ENTRADA'
          ? this.mensagens.recebimento_erro
          : this.mensagens.pagamento_erro;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: msg,
        });
      },
    });
  }

  private processarBatch(): void {
    this.processandoBatch = true;

    const primeiroTitulo = this.titulosSelecionados[0];
    const valor = this.valorDigitado;
    const tiposDeCobranca = this.distribuirValorEntreTitulos(valor);

    const body = this.construirMovimentoBaixa(
      valor,
      primeiroTitulo.pessoaId || primeiroTitulo.pessoa_id || this.pessoaId,
      this.extrairMoeda(primeiroTitulo.moeda),
      tiposDeCobranca
    );

    from([body]).pipe(
      concatMap(b => this.tituloService.gerenciarTituloAberto(b)),
      toArray(),
    ).subscribe({
      next: (resultados) => {
        this.processandoBatch = false;
        this.dialogVisible = false;
        this.titulosSelecionados = [];
        const msg = this.tipoOperacao === 'ENTRADA'
          ? this.mensagens.recebimento_batch_sucesso
          : this.mensagens.pagamento_batch_sucesso;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: msg.replace('{count}', String(resultados.length)),
        });
        this.carregarTitulos();
      },
      error: () => {
        this.processandoBatch = false;
        this.dialogVisible = false;
        const msg = this.tipoOperacao === 'ENTRADA'
          ? this.mensagens.recebimento_erro
          : this.mensagens.pagamento_erro;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: msg,
        });
        // Recarregar mesmo em caso de erro (alguns podem ter sido processados)
        this.carregarTitulos();
      },
    });
  }

  /**
   * Distribui o valor parcial entre os titulos selecionados.
   * Logica identica ao mobile: preenche titulo por titulo ate esgotar o valor.
   */
  private distribuirValorEntreTitulos(valorTotal: number): { tituloId: number; valor: number }[] {
    const cobrancas: { tituloId: number; valor: number }[] = [];
    let restante = valorTotal;

    for (const titulo of this.titulosSelecionados) {
      if (restante <= 0) break;
      const saldo = titulo.saldo || titulo.valor;
      if (restante >= saldo) {
        cobrancas.push({ tituloId: titulo.id, valor: saldo });
        restante -= saldo;
      } else {
        cobrancas.push({ tituloId: titulo.id, valor: restante });
        restante = 0;
      }
    }
    return cobrancas;
  }

  // --- Helpers da tabela ---

  /** Soma valorMovimento (ou valor como fallback) de cada tipoDeCobranca */
  calcularTotalPago(titulo: TituloDTO): number {
    if (!titulo.tipoDeCobranca || titulo.tipoDeCobranca.length === 0) return 0;
    return titulo.tipoDeCobranca.reduce((sum, pag) => sum + (pag.valorMovimento || pag.valor || 0), 0);
  }

  /** Retorna o simbolo da moeda do titulo */
  getMoedaSimbolo(moeda: any): string {
    const m = this.extrairMoeda(moeda);
    if (m === 'DOLAR') return '$';
    if (m === 'REAL') return 'R$';
    return '₲';
  }

  /** Retorna o label da coluna Registro conforme tipoOperacao */
  get labelRegistro(): string {
    return this.tipoOperacao === 'ENTRADA' ? this.labels.registro_recebidos : this.labels.registro_pagos;
  }

  /** Retorna o label da coluna Pago/Recebido conforme tipoOperacao */
  get labelPagoRecebido(): string {
    return this.tipoOperacao === 'ENTRADA' ? this.labels.recebido : this.labels.pago_label;
  }

  private getSituacao(titulo: TituloDTO): string {
    const sit = titulo.situacaoDeCobranca;
    if (!sit) return 'ABERTO';
    if (typeof sit === 'object' && (sit as any)?.enum) return (sit as any).enum.toString().toUpperCase();
    return sit.toString().toUpperCase();
  }

  isAberto(titulo: TituloDTO): boolean {
    return this.getSituacao(titulo) === 'ABERTO';
  }

  isPago(titulo: TituloDTO): boolean {
    return this.getSituacao(titulo) === 'PAGO';
  }

  /** Verifica se o titulo esta cancelado */
  isCancelado(titulo: TituloDTO): boolean {
    return this.getSituacao(titulo) === 'CANCELADO';
  }

  /** Verifica se o titulo tem devolucao */
  hasDevolvido(titulo: TituloDTO): boolean {
    return (titulo.valorDevolvido || 0) > 0;
  }

  // --- Navegacao ---

  voltar(): void {
    // Aberta por outra tela (Clientes/Fornecedores ou as do contador): volta para ela.
    const destino = this.origem ? DESTINO_POR_ORIGEM[this.origem] : null;
    if (destino) {
      const extras = this.origem === 'contador-operar' && this.clienteDaOrigem
        ? { queryParams: { clienteId: this.clienteDaOrigem } }
        : {};
      this.router.navigate([destino.rota], extras);
      return;
    }
    this.router.navigate(['/contas-pagar-receber'], {
      queryParams: { tipo: this.tipoOperacao },
    });
  }

  /**
   * Indica se o titulo tem origem em uma nota/fatura. O backend preenche
   * `nota_id` no TituloDTO apenas quando o titulo foi gerado por uma nota
   * (venda/compra). So nesses casos exibimos o icone "ver fatura".
   */
  temOrigemNota(titulo: TituloDTO): boolean {
    return titulo.nota_id != null;
  }

  /**
   * Navega para a tela de detalhe da fatura que originou o titulo. A rota
   * `notas-list/fatura/:id` espera o id da nota (o mesmo `nota_id` que o
   * backend retorna no titulo).
   */
  irParaFatura(titulo: TituloDTO): void {
    if (titulo.nota_id == null) {
      return;
    }
    this.router.navigate(['/notas-list/fatura', titulo.nota_id]);
  }

  /**
   * Indica se o titulo tem origem em uma despesa acessoria do contador
   * (`despesaacessoria_id` preenchido pelo backend).
   */
  temOrigemDespesaContador(titulo: TituloDTO): boolean {
    return titulo.despesaacessoria_id != null;
  }

  /**
   * Indica se o titulo tem origem na cobranca mensal de um contrato do contador.
   * So exibimos o icone de contrato quando NAO ha despesa acessoria associada
   * (a despesa tem prioridade de navegacao, conforme regra da feature).
   */
  temOrigemContratoContador(titulo: TituloDTO): boolean {
    return titulo.contratocontador_id != null && titulo.despesaacessoria_id == null;
  }

  /**
   * Navega para a tela de despesas acessorias do contador. O destino depende da
   * perspectiva da rota atual:
   * - ENTRADA (contas a receber, perspectiva do CONTADOR): `/contador/operar`,
   *   onde o contador gerencia as despesas acessorias do cliente.
   * - SAIDA (contas a pagar, perspectiva do CLIENTE): `/contador/notificacoes`,
   *   onde o cliente ve/aprova as despesas acessorias lancadas pelo contador.
   */
  irParaDespesaContador(titulo: TituloDTO): void {
    if (titulo.despesaacessoria_id == null) {
      return;
    }
    if (this.tipoOperacao === 'ENTRADA') {
      // Contador: leva a `/contador/operar` abrindo direto na aba
      // "Contrato e Despesas" (`aba=contratos-gastos`) e ja pre-selecionando o
      // cliente quando o backend informou o `empresacliente_id` (casa com
      // empresaIndicadaId).
      const queryParams: { aba: string; clienteId?: number } = { aba: 'contratos-gastos' };
      if (titulo.empresacliente_id != null) {
        queryParams.clienteId = titulo.empresacliente_id;
      }
      this.router.navigate(['/contador/operar'], { queryParams });
      return;
    }
    // Cliente: ve as despesas acessorias do contador em `/contador/notificacoes`.
    this.router.navigate(['/contador/notificacoes']);
  }

  /**
   * Navega para a "tela do contador" de origem do contrato. O destino depende da
   * perspectiva da rota atual (mesmo criterio de `irParaDespesaContador`):
   * - ENTRADA (contas a receber, perspectiva do CONTADOR): `/contador/area`,
   *   onde o contador gerencia seus contratos.
   * - SAIDA (contas a pagar, perspectiva do CLIENTE): `/contador/notificacoes`,
   *   painel de relacionamento do cliente com o seu contador.
   */
  irParaContratoContador(titulo: TituloDTO): void {
    if (titulo.contratocontador_id == null) {
      return;
    }
    if (this.tipoOperacao === 'SAIDA') {
      // Cliente: ve o relacionamento com o contador em `/contador/notificacoes`.
      this.router.navigate(['/contador/notificacoes']);
      return;
    }
    // Contador: area do contador (comportamento atual).
    this.router.navigate(['/contador/area']);
  }

  // --- Formatar data ---

  /**
   * Formata a data de referência (competência) do título como `MM/AAAA`.
   * Recebe ISO `yyyy-MM-dd` e formata por split de string (evita o shift de
   * fuso do `Date`, que interpretaria o ISO como UTC). `-` quando null/vazio.
   */
  formatarMesReferencia(dataReferencia: string | null | undefined): string {
    if (!dataReferencia) return '-';
    const partes = dataReferencia.split('-');
    if (partes.length < 2) return '-';
    const [ano, mes] = partes;
    return `${mes}/${ano}`;
  }

  formatarVencimento(vencimento: string): string {
    if (!vencimento) return '-';
    try {
      const date = new Date(vencimento);
      if (isNaN(date.getTime())) return vencimento;
      const lang = this.translate.currentLang || 'pt-BR';
      const locale = lang === 'es' ? 'es-PY' : 'pt-BR';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return vencimento;
    }
  }

  /**
   * Formata um valor monetario de acordo com a moeda informada, replicando
   * EXATAMENTE o padrao da listagem-pai (`lista-despesas.formatarValor`):
   * Intl.NumberFormat + simbolo "Gs"/"R$"/"$" + casas decimais condicionais.
   * Objetivo: valores identicos entre a tela-pai e este detalhe
   * (ex.: "Gs 3.232" em vez de "₲3,232").
   */
  formatarValor(valor: number | null | undefined, moeda: any): string {
    if (valor == null || isNaN(valor as number)) return '-';
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      GUARANI: { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      REAL: { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      DOLAR: { locale: 'en-US', simbolo: '$', decimais: true },
      PESO: { locale: 'es-AR', simbolo: '$', decimais: true },
    };
    const config = configuracoes[moedaStr] ?? configuracoes['GUARANI'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(Math.abs(valor as number));
    return `${config.simbolo} ${valorFormatado}`;
  }

  // --- Breadcrumb ---

  /** Raiz do breadcrumb: tela de origem (Clientes/Fornecedores) ou "Contas a Receber"/"Contas a Pagar" conforme o tipo. */
  get breadcrumbRaiz(): string {
    if (this.origem) {
      return this.translate.instant(DESTINO_POR_ORIGEM[this.origem].rotulo);
    }
    const key = this.tipoOperacao === 'ENTRADA'
      ? 'contas-cobrar.toggle.contas_a_receber'
      : 'contas-cobrar.toggle.contas_a_pagar';
    return this.translate.instant(key);
  }

  // --- Recorte de filtro ativo (empty state contextual) ---

  /**
   * Detecta o periodo "Mostrar todos" (sentinela 01/01/2000 -> 31/12/3000 que
   * a tela-pai propaga como showAll). Nesses casos nao ha recorte real de data.
   */
  get periodoEhTodos(): boolean {
    // Escolha interativa tem prioridade: "Mostrar todos" (showAll) = sem recorte.
    if (this.periodoTipoAtivo !== null) return this.periodoTipoAtivo === 'showAll';
    if (!this.dataHoraInicial || !this.dataHoraFinal) return true;
    const ini = new Date(this.dataHoraInicial);
    const fim = new Date(this.dataHoraFinal);
    if (isNaN(ini.getTime()) || isNaN(fim.getTime())) return true;
    return ini.getFullYear() <= 2001 && fim.getFullYear() >= 2999;
  }

  /** True quando ha algum recorte real (status, conta ou periodo delimitado). */
  get hasFiltroAtivo(): boolean {
    return !!this.situacao || !!this.contaId || !this.periodoEhTodos;
  }

  /** Limpa o recorte de filtros e recarrega a lista completa de titulos da pessoa. */
  limparFiltros(): void {
    this.situacao = '';
    this.contaId = null;
    this.dataHoraInicial = '';
    this.dataHoraFinal = '';
    this.periodoTipoAtivo = null;
    this.periodoTipoHerdado = null;
    // Reflete "Mostrar todos" no controle (que so leu [valorPadrao] no init):
    // reseta a selecao interna e o rotulo do botao para o preset padrao.
    if (this.periodoFiltro) {
      this.periodoFiltro.selectedValue = 'showAll';
      this.periodoFiltro.textoExibicao = '';
      this.periodoFiltro.textoPadrao = this.translate.instant('dashboard.filtros.mostrar_todos');
    }
    this.first = 0;
    this.page = 0;
    this.carregarTitulos();
  }

}

